import React, {useMemo, useState} from 'react';
import {Box, Button, Collapse} from "@mui/material";
import {Typography} from "@mui/joy";
import CircularProgress from "@mui/joy/CircularProgress";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import {useKeycloak} from "@react-keycloak/web";
import _ from "lodash";
import moment from "moment";
import {toast} from "react-toastify";
import {DescriptionRounded, Graph2Rounded} from "@mui-symbols-material/w300";
import {BoxIcon, Error} from "@dreebit-registry/vsm-microservice-components";
import {InstalledBaseNodeInformation as InstalledBaseNodeInformationQuery} from "../../graphql/queries/installedBaseNodeInformation";
import {UpdateInstalledBaseNode} from "../../graphql/mutations/updateInstalledBaseNode";
import {separateInstalledBaseNodeAttributes} from "../../utils/separateInstalledBaseNodeAttributes";
import {applyInstalledBaseNodeValueRules} from "../../utils/installedBaseNodeFormRules";
import {resolveErrorMessage, sanitizeErrorForDisplay} from "../../utils/resolveErrorMessage";
import {
    getNodeImageChange,
    NODE_IMAGE_FIELD,
    NODE_IMAGE_REFETCH_QUERIES,
    prepareNodeImageFormValues,
} from "../../utils/nodeImage";
import {uploadSingleFile} from "../../utils/uploadSingleFile";
import InstalledBaseTDAForm from "../InstalledBaseTDAForm";
import {withNodeImageInputType} from "../NodeImageInput";
import {FormikHelpersRef} from "../../types";

interface Props {
    nodeId: string;
    /** Write access on the node — read-only users get a disabled form and never
     *  see the Save/Cancel footer (which otherwise appears on unsaved changes). */
    canWrite: boolean;
    /** Switches the panel to the Documents tab (documents stat click-through). */
    onOpenDocuments?: () => void;
    /** Called after a successful update, e.g. to refetch the panel header. */
    onNodeUpdated?: () => void;
}

const StatItem = ({icon, count, label, onClick, testId}: {
    icon: React.ReactNode;
    count: number | string;
    label: string;
    onClick?: () => void;
    testId?: string;
}) => (
    <Box
        data-testid={testId}
        className="flex-row"
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onClick={onClick}
        onKeyDown={onClick ? (e: React.KeyboardEvent) => { if (e.key === 'Enter') onClick(); } : undefined}
        sx={{
            alignItems: 'center',
            gap: '4px',
            ...(onClick ? {
                cursor: 'pointer',
                '&:hover .node-information-stat-label': {textDecoration: 'underline'},
            } : {}),
        }}
    >
        {icon}
        <Typography level="titleSmall">{count}</Typography>
        <Typography level="titleSmallLight" className="node-information-stat-label">{label}</Typography>
    </Box>
);

/**
 * Information tab body of the node detail panel: a compact overview strip
 * (child node / asset / document counts + created/updated dates, identical for
 * every node type) above the embedded node edit form — the same generic TDA
 * form as EditNodeModal, disabled for read-only users. Like the Documents tab,
 * the content scrolls internally; Save/Cancel live in a fixed footer below the
 * scroll area that is only shown while the form has unsaved changes (Formik
 * dirty, surfaced by TDAForm.onDirtyChange) — Cancel discards them, Save
 * persists them, and both make the footer disappear again. TDAForm's own
 * button row stays hidden: its position inside the last attribute category
 * makes it unusable as a sticky footer.
 */
const InstalledBaseNodeInformation = ({nodeId, canWrite, onOpenDocuments, onNodeUpdated}: Props) => {
    const {t} = useTranslation();

    const [formikHelpers, setFormikHelpers] = useState<FormikHelpersRef | null>(null);
    // Footer visibility. Starts false and TDAForm reports false on mount, after
    // resetForm and after the post-save reinitialize (refetch → new initial values).
    const [dirty, setDirty] = useState(false);

    // network-only like EditNodeModal — the form must start from fresh values.
    const {data, loading, error, refetch} = useQuery(InstalledBaseNodeInformationQuery, {
        variables: {id: nodeId},
        fetchPolicy: "network-only",
    });

    const [updateNode, {loading: saving}] = useMutation(UpdateInstalledBaseNode);
    const {keycloak} = useKeycloak();
    // Image upload phase before the mutation — keeps Save disabled meanwhile.
    const [uploading, setUploading] = useState(false);

    const node = data?.installedBaseNode;
    const rawAttributes = _.get(data, 'installedBaseNode.typeDefinitionAttributeList.typeDefinitionAttributes', []);
    // Thumbnail of the current node image for the image input's preview.
    const imagePreviewUrl = node?.image?.file?.thumbnailLink as string | undefined;
    const formAttributes = useMemo(
        () => withNodeImageInputType(rawAttributes, imagePreviewUrl),
        [rawAttributes, imagePreviewUrl]
    );

    const initialFormValues = useMemo<Record<string, any>>(() => {
        if (!rawAttributes.length) return {};
        return rawAttributes.reduce((acc: { [key: string]: any }, attr: any) => {
            acc[attr.index] = attr.value || '';
            return acc;
        }, {});
    }, [rawAttributes]);

    const handleSubmit = async (formValues: any) => {
        // A newly picked node image is uploaded first and its file-service UUID
        // becomes input.imageId; an untouched image is omitted (absent key = keep).
        const initialImageId = initialFormValues[NODE_IMAGE_FIELD];
        const imageChanged = getNodeImageChange(formValues, initialImageId).kind !== 'unchanged';
        let preparedValues: Record<string, any>;
        setUploading(true);
        try {
            preparedValues = await prepareNodeImageFormValues(
                formValues,
                initialImageId,
                (file) => uploadSingleFile(file, keycloak.token)
            );
        } catch (err) {
            toast.error(t('Image upload failed.'));
            return;
        } finally {
            setUploading(false);
        }

        const {input, dynamicLists} = separateInstalledBaseNodeAttributes(preparedValues, rawAttributes);

        return updateNode({
            variables: {
                id: nodeId,
                input,
                ...Object.fromEntries(
                    Object.entries(dynamicLists).map(([key, list]) => [key, list.length > 0 ? list : undefined])
                )
            },
            // Every nameplate / tile / file list showing the image re-reads it.
            refetchQueries: imageChanged ? NODE_IMAGE_REFETCH_QUERIES : undefined,
        }).then(() => {
            toast.success(t('Node has been updated successfully.'));
            // Fresh attribute values re-initialize the form, so it is no longer
            // dirty and Cancel resets to the just-saved state.
            refetch();
            onNodeUpdated?.();
        }).catch((err) => {
            toast.error(resolveErrorMessage(err, t));
        });
    };

    const handleValuesChange = (values: { [key: string]: any }) => {
        applyInstalledBaseNodeValueRules(values, formikHelpers);
    };

    if (loading && !node) {
        return (
            <Box sx={{flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <CircularProgress variant="soft"/>
            </Box>
        );
    }

    if (error && !node) {
        return (
            <Box sx={{flex: 1, minWidth: 0, p: '20px'}}>
                <Error errorObject={sanitizeErrorForDisplay(error, t)}/>
            </Box>
        );
    }

    return (
        <Box
            data-testid="node-detail-information"
            sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                px: '20px',
            }}
        >
            <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', pt: '12px', pb: '12px'}}>
                {/* Overview strip — same icon+count pairing as the tree rows */}
                <Box className="flex-row" sx={{alignItems: 'center', gap: '24px'}}>
                    <StatItem
                        icon={<Graph2Rounded sx={{fontSize: '18px'}}/>}
                        count={node?.childrenNodeCount ?? '-'}
                        label={t('Child nodes')}
                        testId="node-information-stat-children"
                    />
                    <StatItem
                        icon={<BoxIcon size={18}/>}
                        count={node?.assetCount ?? '-'}
                        label={t('Assets')}
                        testId="node-information-stat-assets"
                    />
                    <StatItem
                        icon={<DescriptionRounded sx={{fontSize: '18px'}}/>}
                        count={node?.fileList?.total ?? '-'}
                        label={t('Documents')}
                        onClick={onOpenDocuments}
                        testId="node-information-stat-documents"
                    />
                </Box>
                <Typography level="caption" sx={{color: '#666666', mt: '4px'}}>
                    {`${t('Created')} ${node?.insert ? moment(node.insert).format('L') : '-'}`}
                    {' · '}
                    {`${t('Last updated')} ${node?.update ? moment(node.update).format('L') : '-'}`}
                </Typography>

                <Box sx={{borderBottom: '1px solid', borderColor: 'divider', mt: '12px'}}/>

                {/* Embedded edit form. Submission runs through the footer below via
                    formikHelpers; TDAForm's own button row (rendered because onSubmit
                    is set) sits inside the last attribute category and is hidden.
                    The extra horizontal padding keeps the fields off the full panel
                    width (footer buttons align with it). */}
                <Box sx={{
                    px: '80px',
                    '& form .MuiGrid-container:has(> .MuiGrid-root > button[type="submit"])': {
                        display: 'none',
                    },
                }}>
                    <InstalledBaseTDAForm
                        formStyle={{paddingTop: '4px'}}
                        onSubmit={canWrite ? handleSubmit : undefined}
                        attributes={formAttributes}
                        initialFormValues={initialFormValues}
                        enableReinitialize={true}
                        onValuesChange={handleValuesChange}
                        setFormikHelpersRef={setFormikHelpers}
                        disableForm={!canWrite}
                        onDirtyChange={setDirty}
                    />
                </Box>
            </Box>

            {/* Slides in on the first change instead of popping the scroll area.
                flexShrink sits on the Collapse root because that is the flex child;
                unmountOnExit keeps the buttons (and their testids) out of the DOM
                while there is nothing to save or discard. */}
            <Collapse in={canWrite && dirty} unmountOnExit sx={{flexShrink: 0}}>
                <Box sx={{
                    display: 'flex',
                    gap: '8px',
                    py: '12px',
                    px: '80px',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => formikHelpers?.resetForm()}
                        data-testid="node-information-cancel"
                    >
                        {t('Cancel')}
                    </Button>
                    {/* !dirty: after a successful save the footer is still
                        collapsing while dirty is already false — a click in that
                        window must not resubmit the unchanged form. */}
                    <Button
                        fullWidth
                        variant="contained"
                        type="submit"
                        disabled={saving || uploading || !dirty}
                        onClick={() => formikHelpers?.submit()}
                        data-testid="node-information-save"
                    >
                        {t('Save')}
                    </Button>
                </Box>
            </Collapse>
        </Box>
    );
};

export default InstalledBaseNodeInformation;
